import type { Popup } from 'popups/Popup';
import { Signal } from 'typed-signals';
import type { Bounds, Container } from 'pixi.js';
import { OutOfLivesPopupLayout } from '../../../../../layouts/popups/OutOfLivesPopupLayout';
import type { App } from '../../../../app/App';
import type { PopupElementAnimationData } from '../../../../popups/PopupAnimations';
import { CountDownFormat, formatFixedCountdown } from '../../../../utils/time-utils';
import { EventScheduler } from '../../../../utils/EventScheduler';
import { setHeartLoopAnimation, setRaysLoopAnimation } from './LifePopupAnimations';
import { getLocalizedString } from '../../../../app/localization/getLocalizedString';
import { applyLayoutState, revokeLayoutState } from '../../../../libs/layouts/populateLayouts/applyLayoutState';
import { AskForLivesButtonController } from './buttons/AskForLivesButtonController';
import { BuyLivesButtonController } from './buttons/BuyLivesButtonController';

export type OutOfLivesPopupEvent = 'exit' | 'lives-refilled';

export type OutOfLivesEntryPoint = 'fail-popup' | 'lives-counter' | 'main-menu-play-button';

type OutOfLivesLayoutState = 'game' | 'main-menu' | 'main-menu-max-lives';

export interface OutOfLivesPopupData {
  readonly entryPoint: OutOfLivesEntryPoint;
  readonly showLivesPopup: () => Promise<void>;
}

export class OutOfLivesPopup implements Popup {
  public readonly root: Container;

  public readonly initialBounds: Bounds;

  private readonly layout: OutOfLivesPopupLayout;

  private layoutState?: OutOfLivesLayoutState;

  public readonly onEvent = new Signal<(event: OutOfLivesPopupEvent) => void>();

  private data?: OutOfLivesPopupData;

  private readonly scheduler: EventScheduler = new EventScheduler();

  private readonly buyLivesButtonController: BuyLivesButtonController;

  private readonly askForLivesButton: AskForLivesButtonController | null;

  constructor(private readonly app: App) {
    this.layout = new OutOfLivesPopupLayout();
    this.root = this.layout.root;
    this.initialBounds = this.root.getLocalBounds();
    const {
      platform: { social },
    } = app;

    const { closeButton } = this.layout;

    closeButton.onpointertap = (): void => {
      this.onEvent.emit('exit');
    };

    this.buyLivesButtonController = new BuyLivesButtonController(app, this.layout);
    this.buyLivesButtonController.onLivesPurchased.connect(() => {
      this.onLivesPurchased();
    });

    this.askForLivesButton = social ? new AskForLivesButtonController(app, this.layout) : null;
    this.layout.askForLivesGroup.visible = !!social;

    this.root.on('added', () => {
      this.addStateSubscriptions();
    });

    this.root.on('removed', () => {
      this.scheduler.reset();
      this.removeStateSubscriptions();
    });
  }

  public onAdded(data: OutOfLivesPopupData): void {
    if (!data) return;
    this.data = data;

    this.refreshLayoutState();
    this.buyLivesButtonController.update();

    const { scheduler, layoutState } = this;

    const onSchedulerUpdate = (): void => {
      this.updateNextLifeCountdown();
      if (layoutState !== 'game') this.askForLivesButton?.update();
    };
    this.updateNextLifeCountdown();

    // Add scheduled countdown event.
    scheduler.addEvent(onSchedulerUpdate, 200, true);
    onSchedulerUpdate();

    setRaysLoopAnimation(this.layout);
    this.layout.bigHeart.filters = [];
  }

  public onAnimationInComplete(): void {
    setHeartLoopAnimation(this.layout);
  }

  private refreshLayoutState(): void {
    const entryPoint = this.data?.entryPoint;
    const { stateController } = this.app;
    const currentLayoutState = this.layoutState;

    if (entryPoint === 'fail-popup') this.layoutState = 'game';
    else if (stateController.getLives() >= stateController.getLivesMax()) this.layoutState = 'main-menu-max-lives';
    else this.layoutState = 'main-menu';

    if (this.layoutState !== currentLayoutState) {
      if (currentLayoutState) revokeLayoutState(this.layout, currentLayoutState);
      applyLayoutState(this.layout, this.layoutState);
    }
  }

  public update(deltaTime: number): void {
    this.scheduler.update(deltaTime);
  }

  private updateNextLifeCountdown(): void {
    const { stateController } = this.app;
    const { livesCountdownText } = this.layout;

    if (stateController.getLives() < stateController.getLivesMax()) {
      const timeUntilNextLife = stateController.getTimeUntilNextLife();
      livesCountdownText.text = `${getLocalizedString('Next Life in:')}\n ${formatFixedCountdown(timeUntilNextLife, CountDownFormat.HMS)}`;
    } else {
      livesCountdownText.text = getLocalizedString('Maximum amount of lives');
    }
  }

  private onLivesPurchased(): void {
    this.onEvent.emit('lives-refilled');

    // Popup will be closed here during gameplay.
    if (this.layoutState === 'game') {
      this.removeStateSubscriptions();
      this.scheduler.reset();
      this.root.interactiveChildren = false;
      return;
    }

    this.refreshLayoutState();
  }

  private switchToLoseLifePopup(): void {
    const { scheduler } = this;

    scheduler.reset();
    this.root.interactiveChildren = false;
    this.buyLivesButtonController.enabled = false;

    scheduler.addEvent(async (): Promise<void> => {
      await this.data?.showLivesPopup?.();
    }, 200);
  }

  private addStateSubscriptions(): void {
    const { subscriptions } = this.app.stateController;
    subscriptions.onChange(
      ['lives', 'coins'],
      (): void => {
        const entryPoint = this.data?.entryPoint;

        if (entryPoint === 'fail-popup') {
          this.switchToLoseLifePopup();
          return;
        }

        this.refreshLayoutState();
        this.buyLivesButtonController.update();
        this.updateNextLifeCountdown();
      },
      this,
    );
  }

  private removeStateSubscriptions(): void {
    const { subscriptions } = this.app.stateController;
    subscriptions.removeAllSubscriptions(this);
  }

  public getAnimatedElements(phase: 'in' | 'out'): PopupElementAnimationData[] {
    const {
      popup,
      header,
      closeButton,
      bigHeart,
      bgHexagon,
      rays,
      livesCountdownText,
      buyLivesGroup,
      askForLivesGroup,
    } = this.layout;

    if (phase === 'in') {
      return [
        { container: popup, scaleEase: 'backOut', duration: 400 },
        {
          container: header,
          delay: 300,
          scale: { toX: header.scale.x, toY: header.scale.y },
          scaleEase: 'backOut',
          duration: 400,
        },
        { container: closeButton, delay: 50, scaleEase: 'backOut', duration: 400 },
        {
          container: bgHexagon,
          delay: 20,
          scale: { toX: bgHexagon.scale.x, toY: bgHexagon.scale.y },
          scaleEase: 'backOut',
          duration: 300,
        },
        { container: bigHeart, delay: 20 },
        { container: rays, delay: 0, scale: { toX: rays.scale.x, toY: rays.scale.y }, alpha: 0.5 },
        { container: livesCountdownText, delay: 0, scaleEase: 'backOut', duration: 400 },
        {
          container: buyLivesGroup,
          delay: 0,
          scale: { toX: buyLivesGroup.scale.x, toY: buyLivesGroup.scale.y },
          scaleEase: 'backOut',
          duration: 400,
        },
        {
          container: askForLivesGroup,
          delay: 0,
          scale: { toX: buyLivesGroup.scale.x, toY: buyLivesGroup.scale.y },
          scaleEase: 'backOut',
          duration: 400,
        },
      ];
    }
    return [{ container: popup }];
  }
}
